import { useState, useEffect } from "react";
import { UserPlus, Trash2, KeyRound, Shield, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UserManagement = ({ token }) => {
  const [admins, setAdmins] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newAdmin, setNewAdmin] = useState({ username: "", password: "", role: "admin" });
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers });
      setAdmins(res.data.admins);
    } catch (err) {
      toast.error("Failed to load admins");
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (newAdmin.username.length < 3) { toast.error("Username must be at least 3 characters"); return; }
    if (newAdmin.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users`, newAdmin, { headers });
      toast.success(`Admin '${newAdmin.username}' created`);
      setNewAdmin({ username: "", password: "", role: "admin" });
      setShowCreateForm(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create admin");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Delete admin '${username}'? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${username}`, { headers });
      toast.success(`Admin '${username}' deleted`);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete admin");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/${resetTarget}/reset-password`, { username: resetTarget, new_password: newPassword }, { headers });
      toast.success(`Password reset for '${resetTarget}'`);
      setResetTarget(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-6" data-testid="user-management-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldCheck size={18} className="text-[#518dca]" /> User Management
          <span className="text-xs font-normal text-[#999] ml-1">Super Admin Only</span>
        </CardTitle>
        <Button size="sm" onClick={() => setShowCreateForm(!showCreateForm)} style={{ backgroundColor: "#08263e" }} className="text-white" data-testid="add-admin-btn">
          <UserPlus size={16} className="mr-1" /> Add Admin
        </Button>
      </CardHeader>
      <CardContent>
        {/* Create Form */}
        {showCreateForm && (
          <form onSubmit={handleCreate} className="mb-4 p-4 bg-[#f8f9fa] rounded-lg border" data-testid="create-admin-form">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <input
                type="text" placeholder="Username" value={newAdmin.username}
                onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#518dca]"
                required data-testid="new-admin-username"
              />
              <input
                type="password" placeholder="Password" value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#518dca]"
                required data-testid="new-admin-password"
              />
              <select
                value={newAdmin.role} onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
                className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#518dca] bg-white"
                data-testid="new-admin-role"
              >
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={loading} style={{ backgroundColor: "#08263e" }} className="text-white" data-testid="create-admin-submit">
                {loading ? "Creating..." : "Create Admin"}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowCreateForm(false)}>Cancel</Button>
            </div>
          </form>
        )}

        {/* Admin List */}
        <div className="space-y-2">
          {admins.map((a) => (
            <div key={a.username} className="flex items-center justify-between p-3 bg-white border rounded-lg hover:shadow-sm transition-shadow" data-testid={`admin-row-${a.username}`}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${a.role === "super_admin" ? "bg-amber-100" : "bg-blue-100"}`}>
                  {a.role === "super_admin" ? <ShieldCheck size={16} className="text-amber-600" /> : <Shield size={16} className="text-blue-600" />}
                </div>
                <div>
                  <span className="font-medium text-sm">{a.username}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${a.role === "super_admin" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                    {a.role === "super_admin" ? "Super Admin" : "Admin"}
                  </span>
                  {a.email && <span className="text-xs text-[#999] ml-2">{a.email}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => { setResetTarget(a.username); setNewPassword(""); }} title="Reset Password" data-testid={`reset-pwd-${a.username}`}>
                  <KeyRound size={14} className="text-[#666]" />
                </Button>
                {a.role !== "super_admin" && (
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(a.username)} title="Delete Admin" className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-admin-${a.username}`}>
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </div>
          ))}
          {admins.length === 0 && <p className="text-sm text-[#999] text-center py-4">No admins found</p>}
        </div>

        {/* Reset Password Modal */}
        {resetTarget && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Reset Password: {resetTarget}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setResetTarget(null)}><X size={16} /></Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleResetPassword} className="space-y-3">
                  <input
                    type="password" placeholder="New password (min 6 chars)" value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#518dca]"
                    required data-testid="reset-password-input"
                  />
                  <Button type="submit" size="sm" disabled={loading} style={{ backgroundColor: "#08263e" }} className="w-full text-white" data-testid="reset-password-submit">
                    {loading ? "Resetting..." : "Reset Password"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
